import React, { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { SketchPicker } from 'react-color'

import { anchorTo, bindPopover } from '../../lib/popoverAnchor'
import { isValidCssValue } from '../../lib/cssValue'

/**
 * Color control for the theme tokens: a swatch that opens a picker.
 *
 * WHY IT WAS REBUILT
 * This shipped as a native `<input type="color">` beside a text field. Native
 * is cheap and it cannot express the two values this system needs most:
 *
 *   - `''` -- unset, meaning "the player paints its own default". A native
 *     swatch always shows SOME colour, so unset and "deliberately this exact
 *     colour" looked identical.
 *   - `transparent` -- the shipped default for eight of these tokens and a
 *     value clients legitimately choose. It was reachable only by typing the
 *     word into the text field.
 *
 * THREE STATES, DELIBERATELY DISTINCT
 * Unset, a colour, and transparent are three different stored values with
 * three different meanings, and `hasTheme()` -- which decides whether
 * fill-only fan-out may write into a preference -- keys off the difference
 * between the first and the other two. The old `x` conflated "clear this" with
 * "make this transparent"; they are now separate controls that say what they
 * do.
 *
 * WHAT IS PRESERVED
 * The free-text field. PMM stores these as opaque strings and never validates
 * them, so a client recipe using `rgb()`, `oklch()` or a named colour must
 * round-trip untouched -- the picker cannot represent those, so it defers to
 * the text rather than clobbering it.
 *
 * @param {object} props
 * @param {string} props.value            stored value; '' when unset
 * @param {string} props.fallback         what the player paints when unset
 * @param {string[]} [props.palette]      the theme's own colours, offered as swatches
 * @param {string} props.preferenceName
 * @param {(value: string) => void} props.storeEdits
 */

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

/** Picker height plus its footer, for deciding whether to flip above. */
const POPOVER_H = 340
const POPOVER_W = 240

/**
 * Is this a color the browser can actually paint?
 *
 * PMM stores theme values as opaque strings and explicitly does not
 * validate them, so this is the only gate. It matters beyond cosmetics:
 * a value the player cannot parse still counts as "themed", which opts
 * the preference out of fill-only fan-out while painting nothing.
 *
 * Delegating to the CSS parser accepts every valid form -- hex, rgb(),
 * hsl(), oklch(), named colors -- without us maintaining a grammar.
 * Where `CSS.supports` is unavailable (jsdom under test), fall back to
 * hex so behavior stays defined.
 */
function isValidColor (value) {
  if (value === '') return true
  if (typeof CSS !== 'undefined' && CSS && typeof CSS.supports === 'function') {
    return isValidCssValue('color', value)
  }
  // Under jsdom the shared helper is deliberately permissive about function
  // syntax, which is right for lengths and too loose for a colour swatch.
  return HEX.test(value)
}

/**
 * The value in the shape the picker can actually hold.
 *
 * This is a CONTROLLED input, so whatever it is handed is what its handles
 * show. Handing it a hex string is fine until the user touches the alpha
 * slider: the value becomes `rgba(...)`, no longer matches HEX, and the picker
 * was then handed the opaque fallback instead -- so alpha genuinely applied to
 * the stored value while the handle snapped back to 100%. The control could
 * not represent what it had just produced.
 *
 * react-color takes an `{r,g,b,a}` object as readily as a string, so parse
 * rather than narrow.
 */
function pickerColor (draft, fallback) {
  const rgba = parseRgba(draft)
  if (rgba) return rgba
  // Transparent is alpha 0 -- shown on the slider rather than as a colour the
  // picker cannot place.
  if (isTransparent(draft)) return { r: 0, g: 0, b: 0, a: 0 }
  if (HEX.test(draft)) return draft
  if (HEX.test(fallback || '')) return fallback
  return '#ffffff'
}

/** `rgb()` / `rgba()` -> the object react-color wants. Null for anything else. */
function parseRgba (value) {
  const m = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i
    .exec(typeof value === 'string' ? value.trim() : '')
  if (!m) return null
  return {
    r: Number(m[1]),
    g: Number(m[2]),
    b: Number(m[3]),
    a: m[4] === undefined ? 1 : Number(m[4]),
  }
}

/** `transparent` and a fully-transparent rgba are the same paint. */
function isTransparent (value) {
  if (typeof value !== 'string') return false
  const v = value.trim().toLowerCase()
  return v === 'transparent' || /^rgba\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*0?\.?0*\s*\)$/.test(v)
}

/**
 * What to paint in a swatch for a value the picker may not understand.
 * Anything the browser can parse is handed straight to `background`, so an
 * `oklch()` recipe shows its real colour rather than a placeholder.
 */
function swatchBackground (value) {
  if (!value || isTransparent(value)) return null
  return isValidColor(value) ? value : null
}

const ColorInput = props => {
  const { value, fallback, palette, preferenceName, storeEdits } = props

  // Mirrors the stored value except while the user is mid-keystroke in
  // the text field, where partial input ("#f5f") must not be pushed up
  // as if it were a finished color.
  const [draft, setDraft] = useState(value || '')
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState(null)
  const triggerRef = useRef(null)
  const popoverRef = useRef(null)

  useEffect(() => {
    setDraft(value || '')
  }, [value])

  const isSet = typeof value === 'string' && value !== ''
  const transparent = isTransparent(value)

  // The popover is portalled to <body>. The rail scrolls, and an absolutely
  // positioned panel inside an `overflow: auto` column is clipped by it --
  // which would cut the picker in half for every control near the bottom.
  const place = useCallback(() => {
    setAnchor(anchorTo(triggerRef.current, POPOVER_W, POPOVER_H))
  }, [])

  useEffect(() => {
    if (!open) return undefined
    place()
    return bindPopover({
      onDismiss: () => setOpen(false),
      onReplace: place,
      ignore: [triggerRef, popoverRef]
    })
  }, [open, place])

  // The picker emits an rgba tuple. Alpha below 1 becomes `rgba()` rather
  // than hex -- partner stylesheets use translucent rules (TVW's button
  // outline is `rgba(255,255,255,.15)`), and flattening those to an opaque
  // hex would silently change the design.
  const handlePicker = useCallback(colour => {
    const rgb = colour && colour.rgb
    const next = rgb && typeof rgb.a === 'number' && rgb.a < 1
      ? 'rgba(' + rgb.r + ', ' + rgb.g + ', ' + rgb.b + ', ' + rgb.a + ')'
      : colour.hex
    setDraft(next)
    storeEdits(next)
  }, [storeEdits])

  const handleText = useCallback(ev => {
    const next = ev.target.value
    setDraft(next)
    // Push through only once it parses. Anything else is treated as
    // in-progress typing, so the live preview never flickers through
    // nonsense colors while someone types out "#f5f5f5".
    if (next === '' || isValidColor(next)) storeEdits(next)
  }, [storeEdits])

  // Abandoning a half-typed value reverts the field rather than saving
  // something unpaintable.
  const handleBlur = useCallback(() => {
    if (draft === '' || isValidColor(draft)) {
      if (draft !== value) storeEdits(draft)
    } else {
      setDraft(value || '')
    }
  }, [draft, value, storeEdits])

  const handleSwatchPick = useCallback(next => {
    setDraft(next)
    storeEdits(next)
  }, [storeEdits])

  const trigger = swatchBackground(value) || swatchBackground(fallback)
  const swatchClass = 'theme-color__swatch' +
    (transparent ? ' theme-color__swatch--transparent' : '') +
    (isSet ? '' : ' theme-color__swatch--unset')

  return (
    <div className="theme-color">
      <button
        type="button"
        ref={triggerRef}
        className="theme-color__trigger"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={preferenceName + ' colour'}
        onClick={() => setOpen(prev => !prev)}
      >
        {/* The colour goes on a CHILD, not on this element.
            `background-image` paints above `background-color`, so putting the
            checkerboard in the image layer and the colour in the colour layer
            drew the checks on TOP -- an opaque #ff0000 came out red with grey
            checks over it. The parent carries the checkerboard, the child
            carries the colour and covers it: opaque hides it completely,
            translucent lets it through, which is the whole point. */}
        <span className={swatchClass} aria-hidden="true">
          {trigger && !transparent ? (
            <span className="theme-color__swatch-fill" style={{ background: trigger }}/>
          ) : null}
        </span>
        {/* The stored value verbatim when set. When unset, the player's own
            default -- marked as a default rather than shown as if chosen,
            because a blank field is NOT neutral: many of these tokens fall
            back to another token, so what "default" paints moves with the
            rest of the theme. */}
        <span className={'theme-color__value' + (isSet ? '' : ' theme-color__value--default')}>
          {isSet ? value : (fallback || 'default')}
        </span>
        {!isSet ? <span className="theme-color__tag">default</span> : null}
      </button>

      {open && anchor ? createPortal(
        <div
          className="theme-color__popover"
          ref={popoverRef}
          role="dialog"
          aria-label={preferenceName + ' colour picker'}
          style={{ left: anchor.left + 'px', top: anchor.top + 'px' }}
        >
          <SketchPicker
            color={pickerColor(draft, fallback)}
            onChange={handlePicker}
            presetColors={[]}
            width={POPOVER_W - 20}
          />

          {/* The theme's own colours, so building a palette out of two or
              three values does not mean re-entering a hex six times. */}
          {palette && palette.length ? (
            <div className="theme-color__palette">
              {palette.map(hex => (
                <button
                  key={hex}
                  type="button"
                  className="theme-color__palette-chip"
                  title={hex}
                  aria-label={'Use ' + hex}
                  onClick={() => handleSwatchPick(hex)}
                >
                  <span className="theme-color__swatch-fill" style={{ background: hex }}/>
                </button>
              ))}
            </div>
          ) : null}

          <div className="theme-color__actions">
            {/* Two separate controls because they are two different stored
                values. "Transparent" is a choice the player paints; "Player
                default" is the absence of a choice, and only that one keeps
                the preference eligible for fill-only fan-out. */}
            <button
              type="button"
              className={'theme-color__action' + (transparent ? ' is-active' : '')}
              onClick={() => handleSwatchPick('transparent')}
            >
              <span className="theme-color__swatch theme-color__swatch--transparent" aria-hidden="true"/>
              Transparent
            </button>
            <button
              type="button"
              className="theme-color__action"
              disabled={!isSet}
              onClick={() => handleSwatchPick('')}
              title="Clear this token so the player paints its own default"
            >
              <i className="fa fa-undo" aria-hidden="true"/>
              Player default
            </button>
          </div>

          {/* Any CSS colour, not just what the picker can express. This is
              what lets an `oklch()` or `color-mix()` recipe survive a round
              trip through the editor. */}
          <input
            type="text"
            className="form-control theme-color__text"
            name={preferenceName}
            value={draft}
            placeholder={fallback || 'default'}
            onChange={handleText}
            onBlur={handleBlur}
          />
        </div>,
        document.body
      ) : null}
    </div>
  )
}

export default ColorInput
